import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, UploadCloud, FileText } from "lucide-react";
import Navbar from "../components/Navbar";
import UploadCard from "../components/UploadCard";
import ResultCard from "../components/ResultCard";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

export default function Dashboard() {
  const { user } = useAuth();
  const [currentResult, setCurrentResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/predictions");
      setHistory(data);
    } catch (_e) {
      setHistory([]);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get("/stats");
      setStats(data);
    } catch (_e) {
      /* ignore */
    }
  };

  useEffect(() => {
    loadHistory();
    loadStats();
  }, []);

  const onResult = async (r) => {
    setCurrentResult(r);
    await loadHistory();
    await loadStats();
  };

  const firstName = user?.name?.split(" ")[0] || "Doctor";

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap items-end justify-between gap-4"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-mono-num text-slate-500">Dashboard</div>
            <h1 className="font-heading mt-1 text-4xl sm:text-5xl tracking-tight text-slate-900 font-light">
              Hello, <span className="text-slate-400">{firstName}</span>.
            </h1>
            <p className="mt-2 text-slate-600">Upload a new scan or review your case history below.</p>
          </div>
          <div className="flex gap-3">
            {[
              { k: "Total scans", v: history.length },
              { k: "Validation", v: `${stats?.validation_accuracy ?? 88.7}%` },
              { k: "Latency", v: `${stats?.inference_time_ms ?? 640}ms` },
            ].map((s) => (
              <div key={s.k} className="rounded-xl bg-white border border-slate-200 px-4 py-3 min-w-[120px]">
                <div className="text-[10px] uppercase tracking-[0.22em] font-mono-num text-slate-500">{s.k}</div>
                <div className="mt-1 font-mono-num text-xl text-slate-900">{s.v}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="mt-10 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 space-y-6">
            <UploadCard onResult={onResult} requireAuth={true} />

            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-mono-num text-slate-500">
                <Activity className="h-3.5 w-3.5" /> System
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-mono-num text-slate-500">Agents online</div>
                  <div className="mt-1 text-slate-900 font-medium">4 / 4</div>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-mono-num text-slate-500">Model</div>
                  <div className="mt-1 text-slate-900 font-medium">CNN + ViT</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6">
            {currentResult ? (
              <ResultCard result={currentResult} />
            ) : history[0] ? (
              <ResultCard result={history[0]} />
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center">
                <UploadCloud className="h-8 w-8 mx-auto text-slate-400" />
                <div className="mt-4 font-heading text-xl text-slate-800">No scans yet</div>
                <div className="mt-2 text-sm text-slate-500">Drop your first skin image to see the agent pipeline in action.</div>
              </div>
            )}

            {/* History table */}
            <div className="rounded-2xl bg-white border border-slate-200" data-testid="history-table">
              <div className="p-5 flex items-center justify-between border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-slate-500" />
                  <div className="font-heading text-lg text-slate-900">Case history</div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-mono-num text-slate-500">
                  {history.length} total
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {history.length === 0 && (
                  <div className="p-8 text-center text-sm text-slate-500">No predictions yet.</div>
                )}
                {history.map((h) => (
                  <button
                    key={h.id}
                    onClick={() => setCurrentResult(h)}
                    className="w-full text-left px-5 py-3 hover:bg-slate-50 transition flex items-center justify-between gap-4 cursor-pointer"
                    data-testid={`history-row-${h.id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{h.disease}</div>
                      <div className="text-xs text-slate-500 font-mono-num">
                        {h.image_name || "scan"} · {new Date(h.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.22em] font-mono-num text-slate-500">Conf.</div>
                        <div className="font-mono-num text-slate-900">{h.confidence}%</div>
                      </div>
                      <div className="rounded-full bg-slate-100 text-slate-700 text-[10px] font-mono-num tracking-wider px-2.5 py-1 whitespace-nowrap">
                        {h.severity}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
